import jwtDecode from 'jwt-decode'
import { TDecodedToken, User } from '../types/authTypes'
import dayjs from 'dayjs'
import { TFiltersStrategyProps, TJobsEditTruckData } from '../types/jobsTypes'
import { TRoutesListTransporterData } from '../types/routesTypes'
import { TTransportersDropdownData } from '../types/transportersTypes'
import { ROUTES_STATUS } from '../initialisations/routesInit'

export const PAGE_URLS = {
  LOGIN: '/login',
  DASHBOARD: '/dashboard',
  NOTIFICATION: '/notifications',
  SUPPORT: '/support',
  ROUTE_CALENDAR: '/route-calendar',
  ORGANISATIONS: '/organisations',
  COMPANIES: '/company',
  JOBS: '/jobs',
  ROUTES: '/routes',
  MAP_VIEW: '/map-view',
  ROUTES_PLAN: '/routes-plan',
  TRANSPORTS: '/transports',
  DRIVERS: '/drivers',
  CONTACTS: '/contacts',
  DEPOTS: '/depots',
  AUCTIONS: '/auction',
  JOBS_REPORT: '/reports/job',
  ROUTES_REPORT: '/reports/routes',
  EMAILS_REPORT: '/reports/emails',
  CHANGE_PASSWORD: '/change-password',
  UNAUTHORIZED: '/unauthorized',
  SETTINGS_COMPANIES: '/settings/companies',
  SETTINGS_ORGANISATIONS: '/settings/organisations',
  SETTINGS_ZONES: '/settings/zones',
  SETTINGS_DEPOTS: '/settings/depots',
  SETTINGS_DRIVERS: '/settings/drivers',
  SETTINGS_TRANSPORTERS: '/settings/transports',
  SETTINGS_USERS: '/settings/users',
  SETTINGS_TAGS: '/settings/tags',
  SETTINGS_CUSTOMERS: '/settings/customers',
  SETTINGS_VERSION: '/settings/version',
  DRIVER_DASHBOARD: '/driver-view',
  DRIVER_ROUTES: '/driver-view/routes',
  DRIVER_JOBS: '/driver-view/jobs',
  NO_COMPANY_OPTION: '/options-unauthorised',
  LOGOUT: '/logout',
}

export const API_BASE_URLS = {
  CUSTOMER: '/api/customer-database',
}

export const TWO_FACTOR_STRATEGY = [
  { id: 0, label: 'Forced off, disabled' },
  { id: 1, label: 'Forced on, compulsory' },
  { id: 2, label: 'On, optional per account' },
  { id: 3, label: 'On, enrol user on next login' },
  { id: 4, label: 'On, encourage enrolment with message' },
]

export const UK_POSTCODE_REGEX_PATTERN =
  /^[A-Z]{1,2}[0-9]{1,2}[A-Z]{0,1} ?[0-9][A-Z]{2}$/i

export const jwtDecoder = (token: string) => {
  try {
    var decodedToken: TDecodedToken = jwtDecode(token)

    return {
      token,
      ...decodedToken,
    }
  } catch (_) {
    return null
  }
}

export const parseDataForApi = (obj: any) => {
  // Convert empty strings to nulls + booleans to int, objects to IRI to meet BE requirements
  const convertedObj: any = {}
  for (const key in obj) {
    convertedObj[key] = obj[key]

    // User roles, not parse it!!!
    if (key === 'roles') {
      continue
    }

    // Zone pricing, pass through as-is
    if (key === 'zonePricing') {
      continue
    }

    // Covered postcodes array, pass through as-is
    if (key === 'coveredPostcodes') {
      continue
    }

    if (obj[key] !== null) {
      //Object to IRI
      if (typeof obj[key] === 'object') {
        if (obj[key]['@id'] === undefined && obj[key].length > 0) {
          convertedObj[key] = obj[key].map((item: any) => item['@id'])
          continue
        }

        convertedObj[key] = obj[key]['@id']
        continue
      }

      //empty strings to nulls
      if (typeof obj[key] === 'string' && obj[key].trim() === '') {
        convertedObj[key] = null
      }

      //booleans to int (0/1)
      if (typeof obj[key] === 'boolean') {
        if (obj[key] === true) {
          convertedObj[key] = 1
        } else {
          convertedObj[key] = 0
        }
      }
    }
  }

  return convertedObj
}

export const getTwoFactorStrategyHuman = (twoFactorStrategy: number) => {
  return TWO_FACTOR_STRATEGY.map((strategy) => {
    if (strategy.id === twoFactorStrategy) {
      return strategy.label
    }

    return null
  })
}

export const buildVehicleTitleFromCarwebLookup = (vehicleData: any) => {
  const date = new Date(vehicleData['DateFirstRegistered'])
  const year = date.getFullYear()

  let newVehicleTitle = `${year} `

  if (typeof vehicleData['Combined_Make'] === 'string') {
    newVehicleTitle += `${vehicleData['Combined_Make'].trim()} `
  }

  if (typeof vehicleData['Combined_Model'] === 'string') {
    newVehicleTitle += `${vehicleData['Combined_Model'].trim()} `
  }

  if (typeof vehicleData['ModelSeries'] === 'string') {
    newVehicleTitle += `${vehicleData['ModelSeries'].trim()} `
  }

  if (typeof vehicleData['BodyStyle'] === 'string') {
    newVehicleTitle += `${vehicleData['BodyStyle'].trim()} `
  }

  if (typeof vehicleData['Aspiration'] === 'string') {
    newVehicleTitle += `${vehicleData['Aspiration'].trim()} `
  }

  if (typeof vehicleData['Combined_FuelType'] === 'string') {
    newVehicleTitle += `${vehicleData['Combined_FuelType'].trim()} `
  }

  if (typeof vehicleData['ColourCurrent'] === 'string') {
    newVehicleTitle += `${vehicleData['ColourCurrent'].trim()} `
  }

  return newVehicleTitle.trim()
}

export const priceFormatting = (value: number | string, user?: User) => {
  let currency = 'GBP'
  let numberFormatLocation = 'en-GB'
  if (user && user.companyOptions.includes('SYSTEM_USA')) {
    currency = 'USD'
    numberFormatLocation = 'en-US'
  }

  return new Intl.NumberFormat(numberFormatLocation, {
    style: 'currency',
    currency: currency,
  }).format(Number(value))
}

export const numberFormatting = (value: number | string) => {
  return new Intl.NumberFormat().format(Number(value))
}

const secureRandomInt = (max: number) => {
  const limit = Math.floor(0x100000000 / max) * max
  const buffer = new Uint32Array(1)
  do {
    crypto.getRandomValues(buffer)
  } while (buffer[0] >= limit)

  return buffer[0] % max
}

const secureRandomChar = (chars: string) =>
  chars.charAt(secureRandomInt(chars.length))

export const generatePassword = () => {
  const passwordLength = 12
  const symbols = '!@#$%^&*()'
  const numbers = '0123456789'
  const lowercase = 'abcdefghijklmnopqrstuvwxyz'
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const allChars = symbols + numbers + lowercase + uppercase

  const chars = [
    secureRandomChar(symbols),
    secureRandomChar(numbers),
    secureRandomChar(lowercase),
    secureRandomChar(uppercase),
  ]
  while (chars.length < passwordLength) {
    chars.push(secureRandomChar(allChars))
  }

  for (let i = chars.length - 1; i > 0; i--) {
    const j = secureRandomInt(i + 1)
    ;[chars[i], chars[j]] = [chars[j], chars[i]]
  }

  return chars.join('')
}

export const capitalizeString = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

export const handleCheckMatchingStrings = (
  string1: string,
  string2: string
) => {
  if (string1 !== string2) {
    return false
  }

  return true
}

export const todayTomorrowDateFormat = (dateToFormat: number | string) => {
  if (typeof dateToFormat === 'string') {
    dateToFormat = dayjs(dateToFormat).unix()
  }

  const today = dayjs().endOf('day').unix()
  const tomorrow = dayjs().add(1, 'days').endOf('day').unix()
  const yesterday = dayjs().subtract(1, 'days').startOf('day').unix()

  if (dateToFormat < yesterday) {
    return dayjs.unix(dateToFormat).format('DD/MM/YYYY')
  } else if (dateToFormat < today) {
    return `Today`
  } else if (dateToFormat < tomorrow) {
    return `Tomorrow`
  }

  return dayjs.unix(dateToFormat).format('DD/MM/YYYY')
}

export const commonListFilteringStrategy = (filters: TFiltersStrategyProps) => {
  let parsedFilter = ''

  const orderFilteredList = filters.order.split(';')
  orderFilteredList.map((filter) => {
    const orderFiltered = filter.split(',')
    if (orderFiltered[0] !== '') {
      parsedFilter += `&order[${orderFiltered[0]}]=${orderFiltered[1]}`
    }

    return null
  })

  if (filters.tags.length > 0) {
    const newTagsParsed = filters.tags.map((tag) => {
      const tagIri: string = tag.split(':')[0]
      const tagId = tagIri.split('/').slice(-1)
      return tagId
    })

    parsedFilter += `&tags=${newTagsParsed}`
  }

  return parsedFilter
}

export const parsePhoneNumber = (telNo: string) => {
  if (!telNo || telNo === '') return ''

  if (telNo.startsWith('+44')) {
    telNo = telNo.replace('+44', '0')
  }

  const telNoParsed = telNo.replace(/ /g, '')
  if (isNaN(telNoParsed)) {
    return ''
  }

  return telNoParsed
}

export const hexColorParser = (color: string) => {
  return color.startsWith('#') ? color : `#${color}`
}

export const getTruckDisplayInfo = (
  runTruck:
    | TJobsEditTruckData
    | TRoutesListTransporterData
    | TTransportersDropdownData
) => {
  let truckInfo = `${runTruck.regNo} ${runTruck.name}`
  if (runTruck.transporterVehicleCapacity) {
    truckInfo += ` (${runTruck.transporterVehicleCapacity} car)`
  }

  return truckInfo
}

export const parseIriToId = (iri: string | null) => {
  if (!iri) {
    return 0
  }

  return Number(iri.split('/').slice(-1)[0])
}

export const isValidPostcode = (postcode: string) => {
  postcode = postcode.replace(/\s/g, '')
  return UK_POSTCODE_REGEX_PATTERN.test(postcode)
}

export const getRouteStatusNameByRouteStatusId = (
  routeStatusId: number | null
) => {
  if (!routeStatusId) {
    return 'Not set'
  }

  const status = ROUTES_STATUS.filter((stat) => stat.id === routeStatusId)

  return (
    status[0].label.charAt(0).toUpperCase() +
    status[0].label.slice(1).toLowerCase()
  )
}

export const pluralize = (count: number, single: string, plural: string) => {
  return count > 1 ? plural : single
}

export const copyToClipboard = async (content: string) => {
  try {
    await navigator.clipboard.writeText(content)
    return 'Text copied to clipboard.'
  } catch (error) {
    return 'Copy to clipboard is not allowed on your device.'
  }
}

export const convertMinutesToHours = (minutes: number) => {
  const negativeTime = minutes < 0

  if (negativeTime) {
    minutes = Math.abs(minutes)
  }

  if (minutes < 60) {
    return `${negativeTime ? '-' : '+'}${minutes}m`
  }

  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60

  if (hours > 720) {
    return '> 30 days'
  }

  return `${negativeTime ? '-' : '+'}${hours}h ${mins}m`
}

export const isCollectorUSAVersion = (user: User) => {
  if (user.companyOptions.includes('SYSTEM_USA')) {
    return true
  }

  return false
}

export const buildCustomerAddressForUsa = (
  customerAddress1: string,
  customerAddress2: string,
  customerAddress3: string,
  customerAddress4: string,
  customerPostcode: string
) => {
  let customerAddress = ''

  if (customerAddress1 !== '') {
    customerAddress += `${customerAddress1}`
  }

  if (customerAddress2 !== '') {
    customerAddress += `, ${customerAddress2}`
  }

  if (customerAddress3 !== '') {
    customerAddress += `, ${customerAddress3}`
  }

  if (customerAddress4 !== '') {
    customerAddress += `, ${customerAddress4}`
  }

  if (customerPostcode !== '') {
    customerAddress += `, ${customerPostcode}`
  }

  return customerAddress
}

export const buildDateFormat = (
  date: string | number | null,
  usaFormat = false,
  excludeTime = false
) => {
  if (!date) {
    return ''
  }

  if (typeof date === 'string') {
    date = dayjs(date).unix()
  }

  if (excludeTime) {
    return !usaFormat
      ? dayjs.unix(date).format('DD/MM/YYYY')
      : dayjs.unix(date).format('MM/DD/YYYY')
  }

  return dayjs
    .unix(date)
    .format(usaFormat ? 'MM/DD/YYYY HH:mm' : 'DD/MM/YYYY HH:mm')
}

export const calculateGuaranteedPrice = (
  sqrPrice: string,
  charityAmount: number | null,
  user?: User
) => {
  if (sqrPrice === '') {
    return ' '
  }

  let parsedCharityAmount = charityAmount ?? 0

  return priceFormatting(Number(sqrPrice) - parsedCharityAmount, user)
}

export const isValidHttpUrl = (value: string) => {
  try {
    const url = new URL(value)
    return url.protocol === 'https:'
  } catch {
    return false
  }
}
